import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { API } from "@/lib/auth";
import { api } from "@/lib/api";
import { Modal, Btn, Input, Select, Badge, Card } from "@/components/UI";
import MultiFileDropzone from "@/components/MultiFileDropzone";
import { AlertTriangle, CheckCircle2, Eye, FileText, Loader2, Search, Sparkles, X } from "lucide-react";

const DOC_TYPES = ["SDS", "TDS", "SPECIFICATION", "IFRA", "ALLERGEN", "COA", "SAMPLE_COA", "CERTIFICATE", "OTHER"];

const STATUS_META = {
  review_required: { label: "Onay Bekliyor", cls: "bg-amber-100 text-amber-800 border-amber-300" },
  conflict: { label: "Çelişki", cls: "bg-rose-100 text-rose-800 border-rose-300" },
  unmatched: { label: "Eşleşmedi", cls: "bg-slate-100 text-slate-700 border-slate-300" },
  duplicate: { label: "Zaten Mevcut", cls: "bg-sky-100 text-sky-700 border-sky-300" },
  parse_failed: { label: "Okunamadı", cls: "bg-rose-50 text-rose-700 border-rose-200" },
  failed: { label: "Hata", cls: "bg-rose-200 text-rose-900 border-rose-400" },
  committed: { label: "Kaydedildi", cls: "bg-emerald-100 text-emerald-800 border-emerald-300" },
};

const CONF_META = {
  HIGH: { label: "Yüksek", cls: "bg-emerald-50 text-emerald-700 border-emerald-300" },
  MEDIUM: { label: "Orta", cls: "bg-amber-50 text-amber-700 border-amber-300" },
  LOW: { label: "Düşük", cls: "bg-slate-50 text-slate-700 border-slate-300" },
  CONFLICT: { label: "Çelişki", cls: "bg-rose-100 text-rose-800 border-rose-300" },
};

/**
 * MaterialPicker — searchable combobox over materials list.
 */
function MaterialPicker({ materials, value, onChange, testid }) {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState("");
  const selected = materials.find(m => m.id === value);
  const filtered = useMemo(() => {
    const needle = q.trim().toLowerCase();
    if (!needle) return materials.slice(0, 60);
    return materials.filter(m =>
      (m.name || "").toLowerCase().includes(needle)
      || (m.code || "").toLowerCase().includes(needle)
    ).slice(0, 60);
  }, [q, materials]);
  return (
    <div className="relative" data-testid={testid}>
      <button
        type="button"
        onClick={() => setOpen(o => !o)}
        className="w-full border border-slate-300 rounded px-2 py-1 text-left text-xs bg-white hover:bg-slate-50 flex items-center gap-1"
      >
        <Search size={10} className="text-slate-400"/>
        <span className="flex-1 truncate">
          {selected ? `${selected.name} (${selected.code || "—"})` : <span className="text-slate-400">Malzeme seç…</span>}
        </span>
      </button>
      {open && (
        <div className="absolute z-50 top-full left-0 mt-1 w-80 bg-white border border-slate-200 rounded shadow-lg p-2">
          <Input autoFocus placeholder="Ara (ad/kod)" value={q} onChange={e => setQ(e.target.value)} className="text-xs mb-1" testid={`${testid}-search`}/>
          <div className="max-h-60 overflow-y-auto">
            {filtered.length === 0 && <div className="text-xs text-slate-400 p-2">Sonuç yok</div>}
            {filtered.map(m => (
              <button
                key={m.id}
                type="button"
                onClick={() => { onChange(m.id); setOpen(false); setQ(""); }}
                className={`w-full text-left text-xs px-2 py-1 rounded hover:bg-indigo-50 ${m.id === value ? "bg-indigo-100 font-semibold" : ""}`}
                data-testid={`${testid}-opt-${m.id}`}
              >
                <span className="truncate">{m.name}</span>
                <span className="text-slate-400 font-mono ml-1">· {m.code || "—"}</span>
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

/**
 * BulkDocumentUploadModal — toplu belge yükleme (AI YOK).
 * Props: open, onClose, onCommitted.
 */
export default function BulkDocumentUploadModal({ open, onClose, onCommitted }) {
  const [phase, setPhase] = useState("upload"); // upload | review | summary
  const [files, setFiles] = useState([]); // raw File[]
  const [busy, setBusy] = useState(false);
  const [session, setSession] = useState(null);
  const [materials, setMaterials] = useState([]);
  const [rowChoices, setRowChoices] = useState({}); // doc_id → user selections
  const [filter, setFilter] = useState("all");
  const [commitResult, setCommitResult] = useState(null);

  useEffect(() => {
    if (!open) return;
    (async () => {
      try {
        const r = await api.get("/materials?limit=20000");
        const list = Array.isArray(r) ? r : (r.items || r.materials || []);
        setMaterials(list);
      } catch { setMaterials([]); }
    })();
  }, [open]);

  const reset = () => {
    setPhase("upload"); setFiles([]); setSession(null);
    setRowChoices({}); setFilter("all"); setCommitResult(null);
  };

  const tryClose = () => {
    if (phase === "review" && session && !commitResult) {
      if (!window.confirm("Henüz kaydedilmemiş belgeler var. Çıkmak istediğinize emin misiniz? Pending dosyalar iptal edilecek.")) return;
      cancelSession();
    }
    reset();
    onClose?.();
  };

  const cancelSession = async () => {
    if (!session) return;
    try { await api.del(`/raw-material-cards/bulk-upload/${session.id}`); } catch {}
  };

  const upload = async () => {
    if (files.length === 0) { alert("En az bir dosya seçin"); return; }
    setBusy(true);
    try {
      const form = new FormData();
      files.forEach(f => form.append("files", f));
      const res = await axios.post(`${API}/raw-material-cards/bulk-upload`, form, {
        headers: { "Content-Type": "multipart/form-data" },
        timeout: 300000,
      });
      setSession(res.data);
      // Seed initial choices from match result + extracted fields
      const choices = {};
      for (const d of res.data.documents || []) {
        if (!d.doc_id) continue;
        choices[d.doc_id] = {
          material_id: d.match?.material_id || "",
          document_type: d.document_type || "OTHER",
          document_date: d.extracted?.document_date || "",
          notes: "",
          lot_id: "",
          selected: d.status === "review_required" && !!d.match?.material_id,
          // Master field toggles (all OFF by default per user rules)
          apply_cas: "",
          apply_inci: [],
          apply_ec: "",
          apply_supplier: "",
          apply_trade_name: "",
          save_alias: "",
        };
      }
      setRowChoices(choices);
      setPhase("review");
    } catch (e) {
      alert(e.response?.data?.detail || e.message || "Yükleme başarısız");
    } finally { setBusy(false); }
  };

  const updateChoice = (docId, patch) => {
    setRowChoices(cs => ({ ...cs, [docId]: { ...cs[docId], ...patch } }));
  };

  const toggleSelected = (docId) => {
    setRowChoices(cs => ({ ...cs, [docId]: { ...cs[docId], selected: !cs[docId]?.selected } }));
  };

  const selectAll = (val) => {
    setRowChoices(cs => {
      const out = { ...cs };
      for (const d of session?.documents || []) {
        if (!d.doc_id) continue;
        if (["duplicate", "failed", "conflict", "unmatched", "parse_failed"].includes(d.status)) continue;
        if (!out[d.doc_id]?.material_id) continue;
        out[d.doc_id] = { ...out[d.doc_id], selected: val };
      }
      return out;
    });
  };

  const previewUrl = (docId) => `${API}/raw-material-cards/bulk-upload/${session.id}/pending/${docId}/preview`;

  const openPreview = async (docId) => {
    try {
      const resp = await fetch(previewUrl(docId), { credentials: "include",
        headers: { Authorization: `Bearer ${localStorage.getItem("token") || ""}` } });
      if (!resp.ok) throw new Error("Önizleme alınamadı");
      const blob = await resp.blob();
      const url = URL.createObjectURL(blob);
      window.open(url, "_blank");
    } catch (e) { alert(e.message); }
  };

  const commit = async () => {
    const items = [];
    for (const d of session.documents || []) {
      if (!d.doc_id) continue;
      const c = rowChoices[d.doc_id];
      if (!c?.selected) continue;
      if (!c.material_id) continue;
      const aiSuggested = d.ai_result?.suggested_material_id || null;
      items.push({
        doc_id: d.doc_id,
        material_id: c.material_id,
        document_type: c.document_type,
        document_date: c.document_date || null,
        notes: c.notes || null,
        lot_id: c.lot_id || null,
        apply_cas: c.apply_cas || null,
        apply_inci: c.apply_inci && c.apply_inci.length ? c.apply_inci : null,
        apply_ec: c.apply_ec || null,
        apply_supplier: c.apply_supplier || null,
        apply_trade_name: c.apply_trade_name || null,
        save_alias: c.save_alias || null,
        ai_suggested_material_id: aiSuggested,
        ai_suggestion_accepted: aiSuggested ? aiSuggested === c.material_id : null,
      });
    }
    if (items.length === 0) { alert("En az bir satır seçin"); return; }
    if (!window.confirm(`${items.length} belge kaydedilecek. Onaylıyor musunuz?`)) return;
    setBusy(true);
    try {
      const res = await api.post(`/raw-material-cards/bulk-upload/${session.id}/commit`, { items });
      setCommitResult(res);
      setPhase("summary");
      onCommitted?.();
    } catch (e) {
      alert(e.response?.data?.detail || "Kayıt başarısız");
    } finally { setBusy(false); }
  };

  const runAi = async (docId) => {
    setRowChoices(cs => ({ ...cs, [docId]: { ...cs[docId], _ai_busy: true } }));
    try {
      const res = await api.post(`/raw-material-cards/bulk-upload/${session.id}/analyze-ai/${docId}`, {});
      setSession(s => ({
        ...s,
        documents: (s.documents || []).map(d =>
          d.doc_id === docId ? { ...d, ai_status: res.ai_status,
                                   ai_result: res.ai_result,
                                   ai_analyzed_at: res.ai_analyzed_at } : d)
      }));
      // Auto-adopt AI suggestion if nothing selected yet
      if (res.ai_status === "suggested" && res.ai_result?.suggested_material_id) {
        setRowChoices(cs => {
          const cur = cs[docId] || {};
          const next = { ...cur, _ai_busy: false };
          if (!cur.material_id) next.material_id = res.ai_result.suggested_material_id;
          return { ...cs, [docId]: next };
        });
      } else {
        setRowChoices(cs => ({ ...cs, [docId]: { ...cs[docId], _ai_busy: false } }));
      }
    } catch (e) {
      alert(e.response?.data?.detail || e.message || "AI analizi başarısız");
      setRowChoices(cs => ({ ...cs, [docId]: { ...cs[docId], _ai_busy: false } }));
    }
  };

  const filteredDocs = useMemo(() => {
    const list = session?.documents || [];
    if (filter === "all") return list;
    if (filter === "high") return list.filter(d => d.confidence === "HIGH" && d.status === "review_required");
    if (filter === "review") return list.filter(d => d.status === "review_required");
    if (filter === "conflict") return list.filter(d => d.status === "conflict");
    if (filter === "unmatched") return list.filter(d => d.status === "unmatched");
    if (filter === "duplicate") return list.filter(d => d.status === "duplicate");
    if (filter === "failed") return list.filter(d => d.status === "failed" || d.status === "parse_failed");
    return list;
  }, [session, filter]);

  const stats = session?.stats || {};
  const selectedCount = Object.values(rowChoices).filter(c => c?.selected && c?.material_id).length;

  return (
    <Modal open={open} onClose={tryClose} title="Toplu Belge Yükle" size="xl">
      {phase === "upload" && (
        <div className="space-y-3" data-testid="bulk-doc-upload-phase">
          <div className="p-3 bg-indigo-50 border border-indigo-200 rounded text-xs text-indigo-900">
            <b>SDS · MSDS · TDS · COA · IFRA · Alerjen · Spesifikasyon · Sertifika</b> belgelerini aynı anda yükleyin.
            Sistem AI kullanmadan dosya adı ve belge içeriğiyle deterministik eşleştirme yapacak;
            kullanıcı onayı olmadan hiçbir belge malzeme kartına bağlanmayacak.
          </div>
          <MultiFileDropzone
            files={files}
            onFilesAdded={(arr) => setFiles(fs => [...fs, ...arr])}
            onRemove={(i) => setFiles(fs => fs.filter((_, idx) => idx !== i))}
            label="Hammadde belgelerini sürükle-bırak veya tıklayarak seç"
            mode="raw"
            testid="bulk-doc-dz"
            accept=".pdf,.png,.jpg,.jpeg,.webp,application/pdf,image/*"
          />
          <div className="flex gap-2 justify-end">
            <Btn variant="secondary" onClick={tryClose} testid="bulk-doc-cancel">İptal</Btn>
            <Btn onClick={upload} disabled={busy || files.length === 0} testid="bulk-doc-start">
              {busy ? <><Loader2 size={12} className="animate-spin inline mr-1"/>Yükleniyor…</> : `Yükle ve Analiz Et (${files.length})`}
            </Btn>
          </div>
        </div>
      )}

      {phase === "review" && session && (
        <div className="space-y-3" data-testid="bulk-doc-review-phase">
          {/* Summary strip */}
          <div className="grid grid-cols-7 gap-2 text-xs">
            <Card className="p-2"><div className="text-[9px] uppercase text-slate-500">Toplam</div><div className="font-mono text-base">{stats.total || 0}</div></Card>
            <Card className="p-2 bg-amber-50"><div className="text-[9px] uppercase text-amber-700">Onay Bekliyor</div><div className="font-mono text-base text-amber-800">{stats.review_required || 0}</div></Card>
            <Card className="p-2 bg-rose-50"><div className="text-[9px] uppercase text-rose-700">Çelişki</div><div className="font-mono text-base text-rose-800">{stats.conflict || 0}</div></Card>
            <Card className="p-2"><div className="text-[9px] uppercase text-slate-500">Eşleşmedi</div><div className="font-mono text-base">{stats.unmatched || 0}</div></Card>
            <Card className="p-2 bg-sky-50"><div className="text-[9px] uppercase text-sky-700">Duplicate</div><div className="font-mono text-base text-sky-800">{stats.duplicate || 0}</div></Card>
            <Card className="p-2 bg-rose-50"><div className="text-[9px] uppercase text-rose-700">Okunamadı</div><div className="font-mono text-base text-rose-800">{(stats.parse_failed || 0) + (stats.failed || 0)}</div></Card>
            <Card className="p-2 bg-emerald-50"><div className="text-[9px] uppercase text-emerald-700">Seçili</div><div className="font-mono text-base text-emerald-800">{selectedCount}</div></Card>
          </div>

          {/* Filter + bulk actions */}
          <div className="flex items-center gap-2 flex-wrap">
            {["all", "high", "review", "conflict", "unmatched", "duplicate", "failed"].map(f => (
              <button key={f} onClick={() => setFilter(f)} data-testid={`bulk-filter-${f}`}
                className={`text-[11px] px-2 py-1 rounded border ${filter === f ? "bg-indigo-600 text-white border-indigo-700" : "bg-white border-slate-300 text-slate-700 hover:border-indigo-400"}`}>
                {f === "all" ? "Tümü" : f === "high" ? "Yüksek Güven" : f === "review" ? "Onay Bekliyor"
                 : f === "conflict" ? "Çelişki" : f === "unmatched" ? "Eşleşmedi"
                 : f === "duplicate" ? "Duplicate" : "Hata"}
              </button>
            ))}
            <div className="flex-1"/>
            <Btn size="sm" variant="secondary" onClick={() => selectAll(true)} testid="bulk-select-all">Tümünü Seç</Btn>
            <Btn size="sm" variant="secondary" onClick={() => selectAll(false)} testid="bulk-select-none">Seçimi Kaldır</Btn>
          </div>

          {/* Table */}
          <div className="border border-slate-200 rounded max-h-[55vh] overflow-y-auto">
            <table className="w-full text-xs">
              <thead className="bg-slate-100 sticky top-0 z-10">
                <tr className="text-left">
                  <th className="p-2 w-8"></th>
                  <th className="p-2">Dosya</th>
                  <th className="p-2 w-20">Tip</th>
                  <th className="p-2 w-56">Malzeme</th>
                  <th className="p-2 w-24">Güven</th>
                  <th className="p-2 w-28">Durum</th>
                  <th className="p-2 w-20">İşlem</th>
                </tr>
              </thead>
              <tbody>
                {filteredDocs.map(d => {
                  const choice = rowChoices[d.doc_id] || {};
                  const canSelect = !["duplicate", "failed", "conflict", "unmatched", "parse_failed"].includes(d.status) && !!choice.material_id;
                  const statusMeta = STATUS_META[d.status] || STATUS_META.failed;
                  const confMeta = CONF_META[d.confidence] || CONF_META.LOW;
                  return (
                    <RowView
                      key={d.doc_id}
                      doc={d}
                      choice={choice}
                      materials={materials}
                      canSelect={canSelect}
                      statusMeta={statusMeta}
                      confMeta={confMeta}
                      onToggle={() => canSelect && toggleSelected(d.doc_id)}
                      onUpdate={(patch) => updateChoice(d.doc_id, patch)}
                      onPreview={() => openPreview(d.doc_id)}
                      onRunAi={() => runAi(d.doc_id)}
                    />
                  );
                })}
              </tbody>
            </table>
          </div>

          <div className="flex gap-2 justify-end">
            <Btn variant="secondary" onClick={tryClose} testid="bulk-review-cancel">İptal</Btn>
            <Btn onClick={commit} disabled={busy || selectedCount === 0} testid="bulk-commit">
              {busy ? <><Loader2 size={12} className="animate-spin inline mr-1"/>Kaydediliyor…</> : `Seçilenleri Kaydet (${selectedCount})`}
            </Btn>
          </div>
        </div>
      )}

      {phase === "summary" && commitResult && (
        <div className="space-y-3" data-testid="bulk-doc-summary-phase">
          <div className="grid grid-cols-4 gap-2">
            <Card className="p-3 bg-emerald-50"><div className="text-[10px] uppercase text-emerald-700">Kaydedildi</div><div className="font-mono text-2xl text-emerald-800" data-testid="summary-committed">{commitResult.summary.committed}</div></Card>
            <Card className="p-3 bg-sky-50"><div className="text-[10px] uppercase text-sky-700">Zaten Mevcut</div><div className="font-mono text-2xl text-sky-800">{commitResult.summary.duplicate}</div></Card>
            <Card className="p-3 bg-rose-50"><div className="text-[10px] uppercase text-rose-700">Başarısız</div><div className="font-mono text-2xl text-rose-800">{commitResult.summary.failed}</div></Card>
            <Card className="p-3"><div className="text-[10px] uppercase text-slate-500">Toplam</div><div className="font-mono text-2xl">{commitResult.summary.total}</div></Card>
          </div>
          <div className="max-h-96 overflow-y-auto border border-slate-200 rounded">
            <table className="w-full text-xs">
              <thead className="bg-slate-100"><tr><th className="p-2 text-left">Doc</th><th className="p-2 text-left">Durum</th><th className="p-2 text-left">Detay</th></tr></thead>
              <tbody>
                {(commitResult.results || []).map(r => (
                  <tr key={r.doc_id} className="border-t border-slate-100">
                    <td className="p-2 font-mono text-[10px]">{r.doc_id.slice(0, 8)}</td>
                    <td className="p-2"><Badge className={STATUS_META[r.status]?.cls}>{STATUS_META[r.status]?.label || r.status}</Badge></td>
                    <td className="p-2 text-slate-600">
                      {r.error && <span className="text-rose-700">{r.error}</span>}
                      {r.document_id && <span>v{r.version} · {Object.keys(r.patches_applied || {}).join(", ") || "yalnız bağlandı"}</span>}
                      {r.existing_document_id && <span>mevcut: {r.existing_document_id.slice(0, 8)}</span>}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className="flex gap-2 justify-end">
            <Btn onClick={() => { reset(); onClose?.(); }} testid="bulk-summary-close">Kapat</Btn>
          </div>
        </div>
      )}
    </Modal>
  );
}


function RowView({ doc, choice, materials, canSelect, statusMeta, confMeta, onToggle, onUpdate, onPreview, onRunAi }) {
  const [expanded, setExpanded] = useState(false);
  const extracted = doc.extracted || {};
  const sources = doc.match?.match_sources || [];
  const sourceLabel = sources.length
    ? sources.map(s => ({
        filename_code_exact: "Kod Tam Eşleşme",
        filename_name_exact: "Ad Tam Eşleşme",
        filename_trade_name_exact: "Ticari Ad Eşleşme",
        filename_doc_alias_exact: "Alias Eşleşme",
        filename_name_contains: "Ad Kısmi",
        content_cas_exact: "CAS Eşleşme",
        content_cas_present: "İçerikte CAS",
        filename_and_cas_agree: "Dosya+CAS Doğrulandı",
      }[s] || s)).join(" + ")
    : "Yok";
  const aiStatus = doc.ai_status || "not_used";
  const aiResult = doc.ai_result;
  const showAiBtn = ["unmatched", "conflict", "parse_failed", "review_required"].includes(doc.status)
                    || doc.confidence === "LOW";
  const lotCandidates = doc.lot_candidates || [];

  return (
    <>
      <tr className="border-t border-slate-100 hover:bg-slate-50">
        <td className="p-2 align-top">
          <input type="checkbox"
                 checked={!!choice.selected}
                 disabled={!canSelect}
                 onChange={onToggle}
                 data-testid={`bulk-row-sel-${doc.doc_id}`}/>
        </td>
        <td className="p-2 align-top">
          <button onClick={() => setExpanded(e => !e)} className="text-left hover:underline" data-testid={`bulk-row-expand-${doc.doc_id}`}>
            <div className="font-medium truncate max-w-xs" title={doc.filename}>{doc.filename}</div>
            <div className="text-[10px] text-slate-400 font-mono">{Math.round((doc.size || 0) / 1024)} KB · {doc.text_source || "—"}</div>
          </button>
        </td>
        <td className="p-2 align-top">
          <select value={choice.document_type || "OTHER"}
                  onChange={e => onUpdate({ document_type: e.target.value })}
                  className="border border-slate-300 rounded px-1 py-0.5 text-xs w-full"
                  data-testid={`bulk-row-type-${doc.doc_id}`}>
            {DOC_TYPES.map(t => <option key={t} value={t}>{t}</option>)}
          </select>
        </td>
        <td className="p-2 align-top">
          <MaterialPicker
            materials={materials}
            value={choice.material_id || ""}
            onChange={(id) => onUpdate({ material_id: id })}
            testid={`bulk-row-mat-${doc.doc_id}`}
          />
          {choice.material_id && doc.match?.material_id && doc.match.material_id !== choice.material_id && (
            <div className="text-[9px] text-amber-700 mt-0.5">Öneriden farklı</div>
          )}
        </td>
        <td className="p-2 align-top">
          <span className={`inline-block text-[10px] px-1.5 py-0.5 rounded border ${confMeta.cls}`} title={doc.confidence_reason}>
            {confMeta.label}
          </span>
          <div className="text-[9px] text-slate-500 mt-0.5">{sourceLabel}</div>
        </td>
        <td className="p-2 align-top">
          <span className={`inline-block text-[10px] px-1.5 py-0.5 rounded border ${statusMeta.cls}`}>{statusMeta.label}</span>
          {doc.conflict && (
            <div className="text-[9px] text-rose-700 mt-0.5" title={doc.confidence_reason}>
              <AlertTriangle size={9} className="inline"/> {doc.confidence_reason}
            </div>
          )}
        </td>
        <td className="p-2 align-top">
          {doc.pending_ref && (
            <button onClick={onPreview} className="text-indigo-600 hover:underline text-[11px] block" data-testid={`bulk-row-preview-${doc.doc_id}`}>
              <Eye size={10} className="inline"/> Görüntüle
            </button>
          )}
          {showAiBtn && (
            <button onClick={onRunAi}
                    disabled={choice._ai_busy || aiStatus === "analyzing"}
                    className="text-fuchsia-600 hover:underline text-[11px] block mt-1 disabled:opacity-50"
                    data-testid={`bulk-row-ai-${doc.doc_id}`}>
              {choice._ai_busy || aiStatus === "analyzing"
                ? <><Loader2 size={10} className="inline animate-spin"/> AI...</>
                : <><Sparkles size={10} className="inline"/> AI ile Kontrol</>}
            </button>
          )}
          {aiStatus === "suggested" && <div className="text-[9px] text-fuchsia-700 mt-0.5">AI önerdi</div>}
          {aiStatus === "failed" && <div className="text-[9px] text-rose-700 mt-0.5">AI başarısız</div>}
        </td>
      </tr>
      {expanded && (
        <tr className="bg-slate-50 border-t border-slate-100" data-testid={`bulk-row-expanded-${doc.doc_id}`}>
          <td colSpan={7} className="p-3">
            <div className="grid grid-cols-2 gap-3 text-xs">
              <div>
                <div className="font-semibold text-slate-700 mb-1">Belgeden Çıkarılan</div>
                <KV label="Ürün Adı" value={extracted.product_name}/>
                <KV label="Ticari Ad / INCI" value={(extracted.inci_candidates || []).join(", ")}/>
                <KV label="CAS" value={(extracted.cas_numbers || []).join(", ")}/>
                <KV label="EC" value={(extracted.ec_numbers || []).join(", ")}/>
                <KV label="Tedarikçi" value={extracted.supplier}/>
                <KV label="Revizyon" value={extracted.revision}/>
                <KV label="Tarih" value={extracted.document_date}/>
                <KV label="Lot/Batch" value={extracted.lot_no}/>
                <div className="text-[10px] text-slate-500 mt-1">Metin kaynağı: {doc.text_source}</div>
                {aiResult && (
                  <div className="mt-3 pt-2 border-t border-fuchsia-200 bg-fuchsia-50/50 p-2 rounded" data-testid={`bulk-row-ai-result-${doc.doc_id}`}>
                    <div className="font-semibold text-fuchsia-700 mb-1 text-[11px] flex items-center gap-1">
                      <Sparkles size={10}/> AI Önerisi (suggestion — kullanıcı onayı gerekli)
                    </div>
                    <KV label="AI Material ID" value={aiResult.suggested_material_id ? (materials.find(m=>m.id===aiResult.suggested_material_id)?.name || aiResult.suggested_material_id) : "—"}/>
                    <KV label="AI CAS" value={aiResult.ai_cas}/>
                    <KV label="AI INCI" value={aiResult.ai_inci}/>
                    <KV label="AI Ürün" value={aiResult.ai_product_name}/>
                    {aiResult.deterministic_vs_ai_cas_conflict && (
                      <div className="text-[10px] text-rose-700 mt-1 p-1 bg-rose-100 rounded border border-rose-300">
                        ⚠ Belgede açık CAS ({(aiResult.det_cas || []).join(", ")}) ile AI CAS ({aiResult.ai_cas}) çelişiyor — deterministic değer korunur
                      </div>
                    )}
                    {aiResult.suggested_material_id && (
                      <div className="flex gap-1 mt-2">
                        <button onClick={() => onUpdate({ material_id: aiResult.suggested_material_id })}
                                className="text-[10px] px-2 py-0.5 rounded bg-fuchsia-600 text-white hover:bg-fuchsia-700"
                                data-testid={`bulk-row-ai-accept-${doc.doc_id}`}>
                          Bu Malzemeyle Eşleştir
                        </button>
                        <span className="text-[10px] text-slate-500 self-center">veya tabloda farklı seçin</span>
                      </div>
                    )}
                  </div>
                )}
              </div>
              <div>
                <div className="font-semibold text-slate-700 mb-1">Master Alan Önerileri</div>
                {!doc.master_suggestions_available && (
                  <div className="text-[11px] text-amber-700 bg-amber-50 border border-amber-200 rounded p-2">
                    Belgeden güvenilir identifier okunamadı — master alan önerisi üretilmedi. Yalnız malzeme bağlantısı yapılacak.
                  </div>
                )}
                {doc.master_suggestions_available && (
                  <div className="space-y-1">
                    {(extracted.cas_numbers || []).map(cas => (
                      <label key={cas} className="flex items-center gap-1 text-[11px]">
                        <input type="radio"
                               name={`cas-${doc.doc_id}`}
                               checked={choice.apply_cas === cas}
                               onChange={() => onUpdate({ apply_cas: cas })}
                               data-testid={`bulk-apply-cas-${doc.doc_id}-${cas}`}/>
                        CAS: <span className="font-mono">{cas}</span>
                      </label>
                    ))}
                    {choice.apply_cas && (
                      <label className="flex items-center gap-1 text-[11px] text-slate-500 ml-4">
                        <input type="checkbox" checked={!choice.apply_cas} onChange={() => onUpdate({ apply_cas: "" })}/>
                        CAS'ı uygulamayacağım
                      </label>
                    )}
                    {(extracted.inci_candidates || []).map(inci => (
                      <label key={inci} className="flex items-center gap-1 text-[11px]">
                        <input type="checkbox"
                               checked={(choice.apply_inci || []).includes(inci)}
                               onChange={(e) => {
                                 const cur = choice.apply_inci || [];
                                 onUpdate({ apply_inci: e.target.checked
                                   ? [...cur, inci]
                                   : cur.filter(x => x !== inci) });
                               }}
                               data-testid={`bulk-apply-inci-${doc.doc_id}-${inci.replace(/\s/g, "_")}`}/>
                        INCI: {inci}
                      </label>
                    ))}
                    {(extracted.ec_numbers || []).map(ec => (
                      <label key={ec} className="flex items-center gap-1 text-[11px]">
                        <input type="radio"
                               name={`ec-${doc.doc_id}`}
                               checked={choice.apply_ec === ec}
                               onChange={() => onUpdate({ apply_ec: ec })}
                               data-testid={`bulk-apply-ec-${doc.doc_id}`}/>
                        EC: {ec}
                      </label>
                    ))}
                    {extracted.supplier && (
                      <label className="flex items-center gap-1 text-[11px]">
                        <input type="checkbox"
                               checked={choice.apply_supplier === extracted.supplier}
                               onChange={e => onUpdate({ apply_supplier: e.target.checked ? extracted.supplier : "" })}/>
                        Tedarikçi: {extracted.supplier}
                      </label>
                    )}
                  </div>
                )}
                <div className="mt-2 pt-2 border-t border-slate-200 space-y-1">
                  <label className="flex items-center gap-1 text-[11px]">
                    <input type="checkbox"
                           checked={!!choice.save_alias}
                           onChange={e => onUpdate({ save_alias: e.target.checked ? doc.filename_canonical : "" })}
                           data-testid={`bulk-save-alias-${doc.doc_id}`}/>
                    "{doc.filename_canonical}" dosya adını belge alias'ı olarak kaydet
                  </label>
                  {choice.document_type === "COA" && (
                    <div className="text-[11px] mt-2 p-2 bg-amber-50 border border-amber-200 rounded">
                      <div className="font-semibold text-amber-800 mb-1">COA Lot Eşleşmesi</div>
                      {lotCandidates.length > 0 ? (
                        <div className="space-y-1">
                          <div className="text-[10px] text-slate-600">
                            Aynı malzeme altında <b>exact</b> lot eşleşmesi bulundu:
                          </div>
                          {lotCandidates.map(lc => (
                            <label key={lc.lot_id} className="flex items-center gap-1" data-testid={`bulk-coa-lot-${doc.doc_id}-${lc.lot_id}`}>
                              <input type="radio" name={`lot-${doc.doc_id}`}
                                     checked={choice.lot_id === lc.lot_id}
                                     onChange={() => onUpdate({ lot_id: lc.lot_id })}/>
                              <span className="font-mono">{lc.lot_code}</span>
                              <span className="text-[10px] text-slate-500">
                                · Stok: {lc.qty_available ?? "?"} · SKT: {lc.expiry_date || "—"}
                              </span>
                            </label>
                          ))}
                          <label className="flex items-center gap-1">
                            <input type="radio" name={`lot-${doc.doc_id}`}
                                   checked={!choice.lot_id}
                                   onChange={() => onUpdate({ lot_id: "" })}/>
                            <span className="text-slate-500">Lot bağlantısı olmadan kaydet</span>
                          </label>
                        </div>
                      ) : (
                        <div>
                          <div className="text-slate-600 text-[10px]">
                            {extracted.lot_no ? `Belgedeki lot "${extracted.lot_no}" bu malzeme altında bulunamadı.` : "Belgede lot bilgisi yok."}
                          </div>
                          <Input value={choice.lot_id || ""} onChange={e => onUpdate({ lot_id: e.target.value })}
                                 placeholder="Manuel Lot ID (opsiyonel)"
                                 className="text-xs mt-1"/>
                          <div className="text-[10px] text-slate-400 mt-0.5">Yeni lot otomatik oluşturulmaz</div>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </div>
            </div>
          </td>
        </tr>
      )}
    </>
  );
}

function KV({ label, value }) {
  if (!value) return null;
  return (
    <div className="text-[11px] flex gap-1">
      <span className="text-slate-500 w-20">{label}:</span>
      <span className="font-medium flex-1">{value}</span>
    </div>
  );
}
